import React, { useState, ChangeEvent, FormEvent } from 'react';
import styles from './CreatePersonaModal.module.css';
import { imageIcon } from '../../assets/icons';

interface FormData {
  name: string;
  description: string;
}

interface PersonaSubmitData extends FormData {
  icon: File | null;
}

interface CreatePersonaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: PersonaSubmitData) => Promise<void>;
}

interface ApiError {
  response?: {
    data?: {
      detail?: string;
      [key: string]: any;
    };
  };
}

function CreatePersonaModal({ isOpen, onClose, onSubmit }: CreatePersonaModalProps): JSX.Element | null {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
  });
  const [icon, setIcon] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleIconChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const files = e.target.files;
    if (files && files[0]) {
      setIcon(files[0]);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await onSubmit({
        ...formData,
        icon
      });
      onClose();
    } catch (err) {
      const apiError = err as ApiError;
      const errorMessage = apiError.response?.data?.detail || 
                         Object.values(apiError.response?.data || {})[0]?.[0] ||
                         'Failed to create persona';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>Create Persona</h2>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className={styles.iconContainer}>
            <label htmlFor="persona-icon-upload" className={styles.iconUpload}>
              {icon ? (
                <img 
                  src={URL.createObjectURL(icon)} 
                  alt="Persona icon" 
                  className={styles.previewIcon}
                />
              ) : (
                <div className={styles.uploadPlaceholder}>
                  <img src={imageIcon} alt="Upload" className={styles.uploadIcon} />
                  <span>Upload image</span>
                </div>
              )}
              <input
                type="file"
                id="persona-icon-upload"
                accept="image/*"
                onChange={handleIconChange}
                style={{ display: 'none' }}
              />
            </label>
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter persona name"
              maxLength={20}
              required
            />
            <span className={styles.charCount}>{formData.name.length}/20</span>
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe your persona's physical appearance and background story."
              maxLength={2048}
              rows={6}
              required
            />
            <span className={styles.charCount}>{formData.description.length}/2048</span>
          </div>

          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.buttons}>
            <button 
              type="submit" 
              className={styles.submitButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Persona'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreatePersonaModal; 